import { useState, useEffect, createContext, useContext } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button.jsx";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.jsx";
import { Alert, AlertDescription } from "@/components/ui/alert.jsx";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.jsx";
import { Input } from "@/components/ui/input.jsx";
import { Label } from "@/components/ui/label.jsx";
import {
  Loader2,
  Mail,
  UserPlus,
  Check,
  X,
  AlertCircle,
  Lock,
  LogOut,
  LogIn,
  Eye,
  EyeOff,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast.js";
import { useAuth } from "@/lib/context/AuthContext.jsx";

const InvitationContext = createContext(null);

function useInvitationLogic() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, logout, refreshUser } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [isAccepting, setIsAccepting] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [invitationData, setInvitationData] = useState(null);
  const [error, setError] = useState(null);
  const [acceptState, setAcceptState] = useState("initial");

  // Password modal states
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);

  const token = searchParams.get("token");

  // Fetch invitation details
  useEffect(() => {
    const fetchInvitation = async () => {
      if (!token) {
        setError("Token d'invitation manquant");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const response = await fetch(
          `${import.meta.env.VITE_API_URL || "http://localhost:8000/api"}/invites/${token}/`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error("Invitation non trouvée ou expirée");
          }
          const errorData = await response.json();
          throw new Error(
            errorData.message ||
              errorData.detail ||
              "Erreur lors du chargement de l'invitation",
          );
        }

        const data = await response.json();
        setInvitationData(data);
        setError(null);
      } catch (error) {
        console.error("Failed to fetch invitation:", error);
        setError(
          error.message ||
            "Une erreur est survenue lors du chargement de l'invitation",
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvitation();
  }, [token]);

  // Accept invitation
  const handleAccept = async (withPassword = false) => {
    if (!token) return;

    try {
      setIsAccepting(true);

      const body = {};
      if (withPassword) {
        body.password = password;
        body.password2 = password2;
        body.first_name = firstName;
        body.last_name = lastName;
      }

      const headers = {
        "Content-Type": "application/json",
      };

      // Add auth token if user is logged in
      if (user) {
        headers["Authorization"] =
          `Bearer ${localStorage.getItem("authToken")}`;
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_URL || "http://localhost:8000/api"}/invites/${token}/accept/`,
        {
          method: "POST",
          headers,
          body: JSON.stringify(body),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();

        // Handle different error scenarios
        if (response.status === 400) {
          const errorMessage =
            errorData.message ||
            errorData.detail ||
            errorData.non_field_errors?.[0] ||
            "";

          if (errorMessage.includes("Password is required")) {
            setAcceptState("needs_password");
            setShowPasswordModal(true);
            return;
          } else if (errorMessage.includes("Authenticated user is required")) {
            setAcceptState("needs_login");
            return;
          } else if (
            errorMessage.includes("does not match the invitation email") ||
            errorMessage.includes(
              "cannot accept an invitation for another user",
            )
          ) {
            setAcceptState("needs_logout");
            return;
          } else if (errorMessage.includes("Passwords don't match")) {
            toast({
              title: "Erreur",
              description: "Les mots de passe ne correspondent pas",
              variant: "destructive",
            });
            return;
          }
        }

        throw new Error(
          errorData.message ||
            errorData.detail ||
            "Erreur lors de l'acceptation de l'invitation",
        );
      }

      // Success
      setAcceptState("success");
      setShowPasswordModal(false);

      // If user is already logged in, refresh their data and redirect to main app
      if (user) {
        toast({
          title: "Invitation acceptée",
          description:
            "Vous avez rejoint l'équipe avec succès. Mise à jour de vos informations...",
        });

        // Refresh user data to update the dropdown with new account info
        await refreshUser();

        // Redirect to main app after a short delay
        setTimeout(() => {
          navigate("/societes", {
            state: {
              message:
                "Invitation acceptée avec succès. Vous avez maintenant accès au nouveau compte.",
              type: "success",
            },
          });
        }, 1500);
      } else {
        // For non-logged-in users, redirect to login
        toast({
          title: "Invitation acceptée",
          description:
            "Vous avez rejoint l'équipe avec succès. Vous pouvez maintenant vous connecter.",
        });

        setTimeout(() => {
          navigate("/login", {
            state: {
              message:
                "Invitation acceptée avec succès. Connectez-vous pour accéder à votre compte.",
              type: "success",
            },
          });
        }, 2000);
      }
    } catch (error) {
      console.error("Failed to accept invitation:", error);
      toast({
        title: "Erreur",
        description:
          error.message ||
          "Une erreur est survenue lors de l'acceptation de l'invitation",
        variant: "destructive",
      });
    } finally {
      setIsAccepting(false);
    }
  };

  // Handle password submission
  const handlePasswordSubmit = () => {
    if (!password || !password2 || !firstName || !lastName) {
      toast({
        title: "Erreur",
        description: "Veuillez remplir tous les champs",
        variant: "destructive",
      });
      return;
    }

    if (password !== password2) {
      toast({
        title: "Erreur",
        description: "Les mots de passe ne correspondent pas",
        variant: "destructive",
      });
      return;
    }

    if (password.length < 8) {
      toast({
        title: "Erreur",
        description: "Le mot de passe doit contenir au moins 8 caractères",
        variant: "destructive",
      });
      return;
    }

    handleAccept(true);
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      toast({
        title: "Déconnexion réussie",
        description: "Vous pouvez maintenant accepter l'invitation.",
      });
      setAcceptState("initial");
    } catch (error) {
      console.error("Logout failed:", error);
      toast({
        title: "Erreur",
        description: "Erreur lors de la déconnexion",
        variant: "destructive",
      });
    }
  };

  // Reject invitation (simple redirect since reject API may not exist)
  const handleReject = async () => {
    if (!token) return;

    // First try to call reject API if it exists
    try {
      setIsRejecting(true);
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || "http://localhost:8000/api"}/invites/${token}/reject/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (response.ok) {
        toast({
          title: "Invitation refusée",
          description: "L'invitation a été refusée.",
        });
      } else if (response.status === 404 || response.status === 405) {
        // If reject endpoint doesn't exist, just show message and redirect
        toast({
          title: "Invitation refusée",
          description: "Vous avez choisi de ne pas accepter l'invitation.",
        });
      } else {
        throw new Error("Erreur lors du refus de l'invitation");
      }
    } catch (error) {
      // If reject API doesn't exist or fails, just show message and redirect
      console.log(
        "Reject API not available or failed, proceeding with simple rejection",
      );
      toast({
        title: "Invitation refusée",
        description: "Vous avez choisi de ne pas accepter l'invitation.",
      });
    } finally {
      setIsRejecting(false);
      // Redirect to login after a short delay regardless of API response
      setTimeout(() => {
        navigate("/login");
      }, 1500);
    }
  };

  return {
    user,
    isLoading,
    setIsLoading,
    isAccepting,
    setIsAccepting,
    isRejecting,
    setIsRejecting,
    invitationData,
    setInvitationData,
    error,
    setError,
    acceptState,
    setAcceptState,
    showPasswordModal,
    setShowPasswordModal,
    password,
    setPassword,
    password2,
    setPassword2,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    showPassword,
    setShowPassword,
    showPassword2,
    setShowPassword2,
    handleAccept,
    handlePasswordSubmit,
    handleLogout,
    handleReject,
  };
}

function InvitationProvider({ children }) {
  const logic = useInvitationLogic();

  return (
    <InvitationContext.Provider value={logic}>
      {children}
    </InvitationContext.Provider>
  );
}

function useInvitation() {
  return useContext(InvitationContext);
}

export default function Invitation() {
  return (
    <InvitationProvider>
      <InvitationComponent />
    </InvitationProvider>
  );
}

function InvitationComponent() {
  const { isLoading, error, acceptState } = useInvitation();

  const navigate = useNavigate();

  if (isLoading) return <InvitationLoading />;
  if (error) return <InvitationError />;
  if (acceptState === "success") return <InvitationSuccess />;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-slate-900 dark:to-slate-800">
      <Card className="w-full max-w-md mx-4">
        <InvitationHeader />
        <CardContent className="space-y-6">
          <InvitationContent />

          {acceptState === "needs_login" ? (
            <InvitationAcceptLogin />
          ) : acceptState === "needs_logout" ? (
            <InvitationAcceptLogout />
          ) : (
            <InvitationAccept />
          )}

          <div className="text-center">
            <Button
              onClick={() => navigate("/login")}
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
            >
              Retour à la connexion
            </Button>
          </div>
        </CardContent>
      </Card>
      <InvitationPasswordDialog />
    </div>
  );
}

function InvitationContent() {
  const { invitationData } = useInvitation();
  return (
    <div className="text-center space-y-4">
      <div className="flex items-center justify-center">
        <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
          <Mail className="h-8 w-8 text-primary" />
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-muted-foreground">Vous avez été invité(e) par</p>
        <p className="text-lg font-semibold text-foreground">
          {invitationData?.invited_by}
        </p>
        {invitationData?.company && (
          <p className="text-sm text-muted-foreground">
            à rejoindre{" "}
            <span className="font-medium">{invitationData.company}</span>
          </p>
        )}
      </div>

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          En acceptant cette invitation, vous pourrez accéder aux
          fonctionnalités de l'équipe.
        </AlertDescription>
      </Alert>
    </div>
  );
}

function InvitationAcceptLogin() {
  const { isRejecting, handleReject } = useInvitation();

  const navigate = useNavigate();

  return (
    <div className="space-y-4">
      <Alert>
        <LogIn className="h-4 w-4" />
        <AlertDescription>
          Cette invitation est destinée à un utilisateur existant. Veuillez vous
          connecter d'abord.
        </AlertDescription>
      </Alert>
      <div className="flex flex-col space-y-3">
        <Button
          onClick={() =>
            navigate("/login", {
              state: {
                redirectTo: `/invitation/?token=${token}`,
                message: "Connectez-vous pour accepter l'invitation",
              },
            })
          }
          className="w-full"
        >
          <LogIn className="h-4 w-4 mr-2" />
          Se connecter
        </Button>
        <Button
          onClick={handleReject}
          disabled={isRejecting}
          variant="outline"
          className="w-full"
        >
          {isRejecting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Refus...
            </>
          ) : (
            <>
              <X className="h-4 w-4 mr-2" />
              Refuser l'invitation
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

function InvitationAcceptLogout() {
  const { user, isRejecting, handleLogout, handleReject } = useInvitation();

  return (
    <div className="space-y-4">
      <Alert variant="destructive">
        <LogOut className="h-4 w-4" />
        <AlertDescription>
          Vous êtes connecté avec un compte différent ({user?.email}).
          Déconnectez-vous pour accepter cette invitation.
        </AlertDescription>
      </Alert>
      <div className="flex flex-col space-y-3">
        <Button onClick={handleLogout} variant="destructive" className="w-full">
          <LogOut className="h-4 w-4 mr-2" />
          Se déconnecter
        </Button>
        <Button
          onClick={handleReject}
          disabled={isRejecting}
          variant="outline"
          className="w-full"
        >
          {isRejecting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Refus...
            </>
          ) : (
            <>
              <X className="h-4 w-4 mr-2" />
              Refuser l'invitation
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

function InvitationAccept() {
  const { isAccepting, isRejecting, handleAccept, handleReject } =
    useInvitation();

  return (
    <div className="flex flex-col space-y-3">
      <Button
        onClick={() => handleAccept()}
        disabled={isAccepting || isRejecting}
        className="w-full"
      >
        {isAccepting ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Acceptation...
          </>
        ) : (
          <>
            <Check className="h-4 w-4 mr-2" />
            Accepter l'invitation
          </>
        )}
      </Button>

      <Button
        onClick={handleReject}
        disabled={isAccepting || isRejecting}
        variant="outline"
        className="w-full"
      >
        {isRejecting ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Refus...
          </>
        ) : (
          <>
            <X className="h-4 w-4 mr-2" />
            Refuser l'invitation
          </>
        )}
      </Button>
    </div>
  );
}

function InvitationHeader() {
  return (
    <CardHeader className="text-center">
      <CardTitle className="flex items-center justify-center gap-2">
        <UserPlus className="h-6 w-6 text-primary" />
        Invitation à rejoindre l'équipe
      </CardTitle>
    </CardHeader>
  );
}

// Password Modal for New Users
function InvitationPasswordDialog() {
  const {
    isAccepting,
    setAcceptState,
    showPasswordModal,
    setShowPasswordModal,
    password,
    setPassword,
    password2,
    setPassword2,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    showPassword,
    setShowPassword,
    showPassword2,
    setShowPassword2,
    handlePasswordSubmit,
  } = useInvitation();

  return (
    <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            Créer votre mot de passe
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          <p className="text-sm text-muted-foreground">
            Vous êtes un nouvel utilisateur. Créez un mot de passe pour votre
            compte.
          </p>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">Prénom</Label>
                <Input
                  id="firstName"
                  type="text"
                  placeholder="Votre prénom"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={isAccepting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Nom</Label>
                <Input
                  id="lastName"
                  type="text"
                  placeholder="Votre nom"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={isAccepting}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Entrez votre mot de passe"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isAccepting}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isAccepting}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password2">Confirmer le mot de passe</Label>
              <div className="relative">
                <Input
                  id="password2"
                  type={showPassword2 ? "text" : "password"}
                  placeholder="Confirmez votre mot de passe"
                  value={password2}
                  onChange={(e) => setPassword2(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !isAccepting) {
                      handlePasswordSubmit();
                    }
                  }}
                  disabled={isAccepting}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword2(!showPassword2)}
                  disabled={isAccepting}
                >
                  {showPassword2 ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              Le mot de passe doit contenir au moins 8 caractères.
            </p>
          </div>

          <div className="flex space-x-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                setShowPasswordModal(false);
                setPassword("");
                setPassword2("");
                setFirstName("");
                setLastName("");
                setAcceptState("initial");
              }}
              disabled={isAccepting}
            >
              Annuler
            </Button>
            <Button
              className="flex-1"
              onClick={handlePasswordSubmit}
              disabled={
                isAccepting ||
                !password ||
                !password2 ||
                !firstName ||
                !lastName
              }
            >
              {isAccepting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Création...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Créer le compte
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function InvitationLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-slate-900 dark:to-slate-800">
      <Card className="w-full max-w-md mx-4">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center space-y-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-muted-foreground text-center">
              Chargement de l'invitation...
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function InvitationError() {
  const { error } = useInvitation();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-slate-900 dark:to-slate-800">
      <Card className="w-full max-w-md mx-4">
        <CardHeader className="text-center">
          <CardTitle className="flex items-center justify-center gap-2 text-destructive">
            <AlertCircle className="h-6 w-6" />
            Erreur
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <Button
            onClick={() => navigate("/login")}
            variant="outline"
            className="w-full"
          >
            Retour à la connexion
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function InvitationSuccess() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-100 dark:from-slate-900 dark:to-slate-800">
      <Card className="w-full max-w-md mx-4">
        <CardHeader className="text-center">
          <CardTitle className="flex items-center justify-center gap-2 text-green-600">
            <Check className="h-6 w-6" />
            Invitation acceptée
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950">
            <Check className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-700 dark:text-green-300">
              Vous avez rejoint l'équipe avec succès. Redirection vers la page
              de connexion...
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    </div>
  );
}
